import React from "react";
import { Button, Input, Radio, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import { generateOptionId } from "../gameTypes";
import TtsAudioControls from "../TtsAudioControls";
import OptionRichText from "../OptionRichText";

const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

type TrueFalseGame = Extract<Game, { type: "true_false" }>;

type Props = {
  game: TrueFalseGame;
  onChange: (game: TrueFalseGame) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const TrueFalseEditor: React.FC<Props> = ({ game, onChange, generateAudio }) => {
  // Defensive: legacy/partial games may arrive without a valid categories
  // tuple. Fall back to the default Verdadero/Falso pair so the editor renders
  // instead of crashing on the array destructure.
  const categories: TrueFalseGame["categories"] =
    Array.isArray(game.categories) && game.categories.length === 2
      ? game.categories
      : [
          { id: generateOptionId(), label: "Verdadero" },
          { id: generateOptionId(), label: "Falso" },
        ];
  const [catA, catB] = categories;

  // Defensive: partial/legacy games may arrive without a statements array.
  // Fall back to an empty list so the map() below doesn't crash.
  const statements: TrueFalseGame["statements"] = Array.isArray(game.statements)
    ? game.statements
    : [];

  const updateCategory = (
    index: 0 | 1,
    patch: Partial<TrueFalseGame["categories"][number]>,
  ) => {
    const next = [...categories] as TrueFalseGame["categories"];
    next[index] = { ...next[index], ...patch };
    onChange({ ...game, categories: next });
  };

  const updateStatement = (
    id: string,
    patch: Partial<TrueFalseGame["statements"][number]>,
  ) => {
    onChange({
      ...game,
      statements: statements.map((s) =>
        s.id === id ? { ...s, ...patch } : s,
      ),
    });
  };

  const addStatement = () => {
    onChange({
      ...game,
      statements: [
        ...statements,
        {
          id: generateOptionId(),
          text: "",
          correctCategoryId: catA.id,
        },
      ],
    });
  };

  const removeStatement = (id: string) => {
    onChange({
      ...game,
      statements: statements.filter((s) => s.id !== id),
    });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>
      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>

      <div
        style={{
          padding: 12,
          border: "1px solid var(--bo-card-border)",
          borderRadius: 6,
        }}
      >
        <label style={{ fontWeight: 600 }}>Opciones y Respuestas</label>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginTop: 8,
          }}
        >
          <div>
            <label>Categoría 1</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input
                value={catA.label}
                onChange={(e) => updateCategory(0, { label: e.target.value })}
                placeholder="Título de categoría"
              />
              <TtsAudioControls
                text={catA.label}
                audioUrl={catA.audio}
                onAudioChange={(url) => updateCategory(0, { audio: url })}
                generateAudio={generateAudio}
              />
            </div>
          </div>
          <div>
            <label>Categoría 2</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input
                value={catB.label}
                onChange={(e) => updateCategory(1, { label: e.target.value })}
                placeholder="Título de categoría"
              />
              <TtsAudioControls
                text={catB.label}
                audioUrl={catB.audio}
                onAudioChange={(url) => updateCategory(1, { audio: url })}
                generateAudio={generateAudio}
              />
            </div>
          </div>
        </div>

        {statements.map((statement, idx) => (
          <div
            key={statement.id}
            style={{
              marginTop: 16,
              padding: 12,
              border: "1px solid var(--bo-card-border)",
              borderRadius: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div style={{ flex: 1 }}>
                <label>Declaración {idx + 1}</label>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    marginTop: 4,
                  }}
                >
                  <OptionRichText
                    value={statement.text}
                    onChange={(html) =>
                      updateStatement(statement.id, { text: html })
                    }
                    placeholder="Declaración para analizar"
                  />
                  <TtsAudioControls
                    text={stripHtml(statement.text)}
                    audioUrl={statement.audio}
                    onAudioChange={(url) =>
                      updateStatement(statement.id, { audio: url })
                    }
                    generateAudio={generateAudio}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 24,
                  paddingTop: 24,
                  minWidth: 200,
                  textAlign: "center",
                }}
              >
                <Radio.Group
                  value={statement.correctCategoryId}
                  onChange={(e) =>
                    updateStatement(statement.id, {
                      correctCategoryId: e.target.value,
                    })
                  }
                  style={{ display: "contents" }}
                >
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      {catA.label || "Categoría 1"}
                    </div>
                    <Radio value={catA.id} />
                  </div>
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      {catB.label || "Categoría 2"}
                    </div>
                    <Radio value={catB.id} />
                  </div>
                </Radio.Group>
              </div>

              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeStatement(statement.id)}
                disabled={statements.length <= 1}
              />
            </div>

            <div style={{ marginTop: 8 }}>
              <label>Imagen</label>
              <ImageUploadInput
                value={statement.image}
                onChange={(url) =>
                  updateStatement(statement.id, { image: url })
                }
              />
            </div>
          </div>
        ))}

        <div style={{ marginTop: 12 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addStatement}
          >
            Agregar declaración
          </Button>
        </div>
      </div>
    </Space>
  );
};

export default TrueFalseEditor;
